import axios, { AxiosInstance, AxiosRequestConfig } from 'axios';
import { AuthTokens } from '../types';
import { getApiEndpoints } from '../config/apiConfig';

// API type: 'api' for main trading gateway
type ApiType = 'api';

class ApiClient {
  private clients: Map<ApiType, AxiosInstance> = new Map();
  private tokens: AuthTokens | null = null;
  private endpoints = getApiEndpoints();

  constructor() {
    this.createClients();
    this.loadTokensFromStorage();
    this.setupInterceptors();
  }

  private createClients() {
    const apiTypes: ApiType[] = ['api'];

    apiTypes.forEach((apiType) => {
      const client = axios.create({
        baseURL: this.endpoints[apiType],
        timeout: 30000,
        headers: {
          'Content-Type': 'application/json',
        },
      });
      this.clients.set(apiType, client);
    });
  }

  private getClient(apiType: ApiType = 'api'): AxiosInstance {
    const client = this.clients.get(apiType);
    if (!client) {
      throw new Error(`No client configured for API type: ${apiType}`);
    }
    return client;
  }

  private loadTokensFromStorage() {
    try {
      const storedTokens = localStorage.getItem('auth_tokens');
      if (storedTokens) {
        this.tokens = JSON.parse(storedTokens);
      }
    } catch (error) {
      console.error('Error loading tokens from storage:', error);
      this.clearTokens();
    }
  }

  private setupInterceptors() {
    this.clients.forEach((client) => {
      client.interceptors.request.use(
        (config) => {
          if (this.tokens?.idToken) {
            config.headers.Authorization = `Bearer ${this.tokens.idToken}`;
          }
          return config;
        },
        (error) => Promise.reject(error)
      );

      client.interceptors.response.use(
        (response) => response,
        (error) => {
          if (error.response?.status === 401) {
            this.clearTokens();
            window.dispatchEvent(new CustomEvent('auth:unauthorized'));
          }
          return Promise.reject(error);
        }
      );
    });
  }

  public setTokens(tokens: AuthTokens) {
    this.tokens = tokens;
    try {
      localStorage.setItem('auth_tokens', JSON.stringify(tokens));
    } catch (error) {
      console.error('Error storing tokens:', error);
    }
  }

  public clearTokens() {
    this.tokens = null;
    try {
      localStorage.removeItem('auth_tokens');
    } catch (error) {
      console.error('Error clearing tokens:', error);
    }
  }

  public getTokens(): AuthTokens | null {
    return this.tokens;
  }

  public isAuthenticated(): boolean {
    return !!this.tokens?.idToken;
  }

  public async get<T>(url: string, config?: AxiosRequestConfig, apiType: ApiType = 'api') {
    const client = this.getClient(apiType);
    const response = await client.get<T>(url, config);
    return response.data;
  }

  public async post<T>(url: string, data?: any, config?: AxiosRequestConfig, apiType: ApiType = 'api') {
    const client = this.getClient(apiType);
    const response = await client.post<T>(url, data, config);
    return response.data;
  }

  public async put<T>(url: string, data?: any, config?: AxiosRequestConfig, apiType: ApiType = 'api') {
    const client = this.getClient(apiType);
    const response = await client.put<T>(url, data, config);
    return response.data;
  }

  public async delete<T>(url: string, config?: AxiosRequestConfig, apiType: ApiType = 'api') {
    const client = this.getClient(apiType);
    const response = await client.delete<T>(url, config);
    return response.data;
  }

  public async patch<T>(url: string, data?: any, config?: AxiosRequestConfig, apiType: ApiType = 'api') {
    const client = this.getClient(apiType);
    const response = await client.patch<T>(url, data, config);
    return response.data;
  }
}

// Create singleton instance
const apiClient = new ApiClient();

export default apiClient;
export type { ApiType };
